// BlitzHeader.jsx - Updated to support mobile actions and user avatar
import React from "react";
import { useNavigate } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Box, useMediaQuery, 
  Tooltip, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from 'react-query';
import { Typography } from '@mui/material';
import blitzLogo from './globalAssets/logo_placeholder.png';
import blitzLogoWhite from './globalAssets/blitzLogo.png-removebg-preview.png';
import routes from "../Config/routes";
import { lightTheme } from "../Utils/baseTheme";
import useAuth from "../Hooks/use-auth";
import { useCreatorAuth } from "../Hooks/creator-use-auth";
import API from "../API";

const BlitzHeader = ({
  menuButton,
  children,
  isDesktop: isDesktopProp,
  mobileActions = []
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const isDesktop = typeof isDesktopProp === 'boolean' ? isDesktopProp : upMd;
  
  const { isAuthorized: userOK, getCurrrentUser } = useAuth();
  const { isAuthorized: creatorOK } = useCreatorAuth();

  // Determine which context is active
  const isCreatorContext = creatorOK;
  const isUserContext = userOK;
  const isAnyAuthorized = isCreatorContext || isUserContext;

  // Fetch creator data with react-query (same pattern as UnifiedProfileSettings)
  const { data: creatorData } = useQuery(
    ['creator'],
    API.creatorConnect.getCreator,
    {
      enabled: isCreatorContext,
      refetchOnWindowFocus: false,
    }
  );

  // Get user avatar and display name (no random placeholders)
  let displayName = "Me";
  let photo; // undefined by default → blank avatar

  if (isCreatorContext) {
    // Creator context - use creator data from API query
    const creator = creatorData?.user?.creator;
    displayName = creator?.display_name || "Creator";
    photo = creator?.pfphref || undefined;
  } else if (isUserContext) {
    // User context - use regular user data
    const u = getCurrrentUser();
    displayName = u?.first_name || u?.last_name
      ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
      : u?.username || displayName;
    photo = u?.phphref || undefined;
  }

  // Handle logo click - redirect based on context
  const handleLogoClick = () => {
    if (!isAnyAuthorized) {
      window.location.href = '/';
    } else if (isCreatorContext && !isUserContext) {
      navigate('/creatorconnect/start');
    } else {
      navigate(routes.dashboard);
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <AppBar
        id='MobileHeader'
        position="fixed"
        sx={{  background: theme.palette.background.default , boxShadow: 'none', color: '#0f172a' }}
      >
        <Toolbar>
          <Box
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
            flex={1}
            maxHeight='100%'
          >
            {isDesktop ? (
              <>
                {/* Desktop: Menu button first, then avatar, then logo */}
                {menuButton}
                {isAnyAuthorized && (
                  <Tooltip title={`Go to ${displayName}'s profile`} placement="bottom">
                    <IconButton
                      color="inherit"
                      onClick={() => navigate('/bio')}
                      sx={{ 
                        ml: 1,
                        "&:hover": { 
                          transform: "scale(1.05)",
                        }
                      }}
                    >
                      <Avatar
                        src={photo}
                        alt={displayName}
                        sx={{ 
                          width: 36, 
                          height: 36,
                          border: "2px solid",
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.shadows[2],
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Box margin={0} flex={1} display="flex" alignItems="center" >
                  <IconButton
                    color="inherit"
                    aria-label="logo"
                    sx={{
                      margin: 0,
                      padding: 0,
                      ml: isAnyAuthorized ? 1 : 1,
                      "&.MuiButtonBase-root:hover": {
                        bgcolor: "transparent"
                      }
                    }}
                    onClick={handleLogoClick}
                  >
                    
                    <img
                      src={blitzLogo}
                      // src={blitzLogoWhite}
                      alt="logo"
                      style={{ height: "45px" }}
                    />
                  </IconButton>
                  
                </Box>
                {children}
              </>
            ) : (
              <>
                {/* Mobile: Avatar first, then logo */}
                {isAnyAuthorized && (
                  <Tooltip title={`Go to ${displayName}'s profile`} placement="bottom">
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={() => navigate('/bio')}
                      sx={{ 
                        mr: 1,
                        "&:hover": { 
                          transform: "scale(1.05)",
                        }
                      }}
                    >
                      <Avatar
                        src={photo}
                        alt={displayName}
                        sx={{ 
                          width: 36, 
                          height: 36,
                          border: "2px solid",
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.shadows[2],
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Box margin={0} flex={1}>
                  <IconButton
                    color="inherit"
                    aria-label="logo"
                    sx={{
                      margin: 0,
                      padding: 0,
                      "&.MuiButtonBase-root:hover": {
                        bgcolor: "transparent"
                      }
                    }}
                    onClick={handleLogoClick}
                  >
                  
                    <img
                      // src={blitzLogoWhite}
                      src={blitzLogo}
                      alt="logo"
                      style={{ height: "45px" }}
                    />
                  </IconButton>
                </Box>
                {children}
                {/* Mobile: Action buttons instead of menu button */}
                {mobileActions.map((action) => (
                  <Tooltip key={action.key} title={action.tooltip} placement="bottom">
                    <IconButton
                      color="inherit"
                      onClick={action.onClick}
                      sx={{ ml: 1 }}
                      aria-label={action.tooltip}
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
};

export default BlitzHeader;